import { ReactNode, createContext, useCallback, useContext, useEffect, useState } from 'react';
import usePermission from '../../hooks/usePermission';
import { IRestaurants } from '../../interfaces/index.types';
import { getDocs, query, where } from '../../libs/firestore-wrapper';
import { getAllFoods, getAllUserMenuCategories } from '../../modals/order';
import { IUserMenuCategory } from '../../pages/CategoriesManagement/categoriesManagement.types';
import { IFoodDetails } from '../../pages/MenuManagement/menu.types';
import { useAuthContext } from '../AuthContext/AuthContext';
import { useSettingsContext } from '../SettingsContext';
import { moveFoodsToCategories } from './categoryOperations';

interface IFoodMenus {
  categories: IUserMenuCategory[];
  foods: IFoodDetails[];
}

interface CategoriesContextValue {
  foodMenu: IFoodMenus;
  loading: boolean;
  selectedIds: string[];
  orderBy: string;
  order: 'asc' | 'desc';
  handleSelectAll: (checked: boolean) => void;
  handleSelect: (id: string) => void;
  clearSelection: () => void;
  handleRequestSort: (property: string) => void;
  refetchData: () => Promise<void>;
  moveFoodsToCategories: (
    selectedFoodIds: string[],
    selectedCategoryIds: string[],
    initialCommonCategoryIds: string[],
    manuallyRemovedCategoryIds?: string[]
  ) => Promise<void>;
}

const init_food_menu: IFoodMenus = {
  categories: [],
  foods: [],
};

const CategoriesContext = createContext<CategoriesContextValue | undefined>(undefined);

export const CategoriesProvider = ({ children }: { children: ReactNode }) => {
  const { userInfo } = useAuthContext();
  const { stadiumData } = useSettingsContext();
  const [loading, setLoading] = useState(true);
  const [foodMenu, setFoodMenu] = useState<IFoodMenus>(init_food_menu);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState<string>('');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const { restaurantId } = usePermission();

  const fetchData = useCallback(async () => {
    const stadiumId = userInfo?.stadium_id ?? stadiumData?.id;
    if (stadiumId) {
      try {
        setLoading(true);
        const data = (await fetchFoodMenu(stadiumId)) as IFoodMenus;
        data.foods.sort((a, b) => {
          if (a.create_time && b.create_time) {
            const aTime = a.create_time.seconds * 1000 + a.create_time.nanoseconds / 1000000;
            const bTime = b.create_time.seconds * 1000 + b.create_time.nanoseconds / 1000000;
            return bTime - aTime;
          } else if (a.create_time) {
            return -1;
          } else if (b.create_time) {
            return 1;
          } else {
            return 0;
          }
        });
        setFoodMenu(data);
        setLoading(false);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    }
  }, [userInfo?.stadium_id, stadiumData?.id]);

  async function fetchFoodMenu(_stadiumId: string) {
    try {
      const categoryQuery = getAllUserMenuCategories(_stadiumId);
      const categoryQuerySnapshot = await getDocs(categoryQuery);
      const categoryDocs = categoryQuerySnapshot.docs;

      const categoryMap = new Map(categoryDocs.map((doc) => [doc.id, doc.data().title]));

      const restaurantIds = userInfo?.restaurants?.map((restaurant: IRestaurants) => restaurant.id) || (restaurantId ? [restaurantId] : []);
      const restaurantMap = new Map(userInfo?.restaurants?.map((r: IRestaurants) => [r.id, r.name]) || []);

      const baseFoodQuery = getAllFoods(_stadiumId);
      const foodQuery = query(baseFoodQuery, where('restaurantId', 'in', restaurantIds));
      const foodQuerySnapshot = await getDocs(foodQuery);

      const allFoods = foodQuerySnapshot.docs.map((foodDoc) => {
        const foodData = foodDoc.data();
        const categoriesData = foodData.categories_menu_ids || [];

        const categoryIds = categoriesData.map((cat: any) => (typeof cat === 'string' ? cat : cat.id));

        const categoryNames = categoryIds
          .map((catId: string) => categoryMap.get(catId))
          .filter((name: string | undefined) => name)
          .join(', ');

        return {
          ...foodData,
          id: foodDoc.id,
          restaurantName: restaurantMap.get(foodData.restaurantId) || '',
          categoryNames,
        };
      });

      const result = {
        categories: categoryDocs.map((categoryDoc) => ({ id: categoryDoc.id, ...categoryDoc.data() })),
        foods: allFoods,
      };
      return result;
    } catch (error) {
      throw error;
    }
  }

  useEffect(() => {
    const stadiumId = userInfo?.stadium_id ?? stadiumData?.id;
    if (stadiumId) {
      fetchData();
    }
  }, [userInfo?.stadium_id, fetchData, stadiumData?.id]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(foodMenu.foods.map((item) => item.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelect = (id: string) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selectedId) => selectedId !== id);
      }
      return [...prev, id];
    });
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const clearSelection = () => {
    setSelectedIds([]);
  };

  const handleMoveFoodsToCategories = async (
    selectedFoodIds: string[],
    selectedCategoryIds: string[],
    initialCommonCategoryIds: string[],
    manuallyRemovedCategoryIds: string[] = []
  ) => {
    const stadiumId = userInfo?.stadium_id;
    if (!stadiumId) throw new Error('No stadium ID');
    await moveFoodsToCategories(
      stadiumId,
      selectedFoodIds,
      selectedCategoryIds,
      foodMenu.categories,
      foodMenu.foods,
      initialCommonCategoryIds,
      manuallyRemovedCategoryIds
    );
    await fetchData();
  };

  return (
    <CategoriesContext.Provider
      value={{
        foodMenu,
        loading,
        selectedIds,
        orderBy,
        order,
        handleSelectAll,
        handleSelect,
        handleRequestSort,
        clearSelection,
        refetchData: fetchData,
        moveFoodsToCategories: handleMoveFoodsToCategories,
      }}
    >
      {children}
    </CategoriesContext.Provider>
  );
};

export const useCategoriesContext = () => {
  const context = useContext(CategoriesContext);
  if (!context) {
    throw new Error('useCategoriesContext must be used within CategoriesProvider');
  }
  return context;
};
